import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { getStoredReferral, clearStoredReferral } from '../utils/referralStorage';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const params = new URLSearchParams(hash.substring(1));
      const sessionId = params.get('session_id');

      if (!sessionId) {
        navigate('/login');
        return;
      }

      try {
        // Forward the predictions-disclaimer acknowledgement that the user
        // ticked on /register before being bounced to Google. The backend
        // only consults it when this OAuth round-trip is creating a brand
        // new user account — existing users keep signing in fine.
        let disclaimerAccepted = false;
        try {
          disclaimerAccepted = localStorage.getItem('forge:disclaimer_accepted') === '1';
        } catch (_e) { /* private mode */ }

        // Same idea for the referral code — if the user hit /register
        // via someone's share link, we forward it so a new Google user
        // gets attributed correctly. Existing users are no-op'd server-side.
        const referralCode = getStoredReferral();

        const response = await axios.post(
          `${API}/auth/session`,
          {
            session_id: sessionId,
            disclaimer_accepted: disclaimerAccepted,
            referral_code: referralCode || null,
          },
          { withCredentials: true }
        );

        // Clean up the flag once it's been consumed.
        try { localStorage.removeItem('forge:disclaimer_accepted'); } catch (_e) { /* noop */ }
        clearStoredReferral();

        setUser(response.data);
        navigate('/dashboard', { state: { user: response.data }, replace: true });
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Auth callback error:', error);
        }
        // If the server refused the OAuth signup because the disclaimer
        // wasn't ticked (e.g. someone hit /auth/callback directly without
        // ticking the box on /register first), surface the message and
        // bounce them back to /register so they can tick it.
        const detail = error?.response?.data?.detail;
        const isDisclaimerBlock = typeof detail === 'string' && detail.toLowerCase().includes('predictions');
        navigate(isDisclaimerBlock ? '/register' : '/login', {
          state: isDisclaimerBlock ? { authError: detail } : undefined,
        });
      }
    };

    processAuth();
  }, [location, navigate, setUser, API]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052FF] mx-auto mb-4"></div>
        <p className="text-[#64748B]">Completing authentication...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
